import { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Calendar } from '../../ui/calendar';
import { Copy, ExternalLink, Trash2, Power, Link2, QrCode, Download, CalendarClock, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ShortenedUrlResponse, deleteUrl, activateUrl, deactivateUrl, updateUrl } from '../../utils/sqrone-api';
import { useAuth } from '../../contexts/AuthContext';

interface UrlListProps {
  urls: ShortenedUrlResponse[];
  onDelete: (shortCode: string) => void;
  onUpdate: (shortCode: string, updates: Partial<ShortenedUrlResponse>) => void;
}

export function UrlList({ urls, onDelete, onUpdate }: UrlListProps) {
  const { token, isAuthenticated } = useAuth();

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleVisit = (url: string) => {
    window.open(url, '_blank');
  };

  const handleDelete = async (shortCode: string) => {
    if (isAuthenticated && token) {
      try {
        await deleteUrl(shortCode, token);
        toast.success('Checkpoint deleted');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete');
        console.error('Delete error:', err);
        return;
      }
    }
    onDelete(shortCode);
  };

  const handleToggleActive = async (url: ShortenedUrlResponse, currentState: boolean) => {
    if (!isAuthenticated || !token || !url.id) {
      return; // Should not happen - UI prevents this
    }

    const newState = !currentState;
    try {
      if (newState) {
        await activateUrl(url.id, token);
      } else {
        await deactivateUrl(url.id, token);
      }
      onUpdate(url.shortCode, { isActive: newState });
      toast.success(newState ? 'Checkpoint activated' : 'Checkpoint deactivated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleSetExpiry = async (url: ShortenedUrlResponse, date: Date | undefined) => {
    if (!isAuthenticated || !token) {
      return; // Should not happen - UI prevents this
    }

    const expiresAt = date?.toISOString();
    try {
      await updateUrl(url.shortCode, { expiresAt: expiresAt || undefined }, token);
      onUpdate(url.shortCode, { expiresAt });
      if (date) {
        toast.success(`Expiry set to ${format(date, 'PPP')}`);
      } else {
        toast.success('Expiry removed');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update expiry');
    }
  };

  const handleDownloadQr = async (qrCodeUrl: string, name: string) => {
    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `qr-${name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toast.success('QR code downloaded');
    } catch {
      toast.error('Failed to download QR code');
    }
  };

  if (urls.length === 0) return null;

  return (
    <div className="mt-8 space-y-4">
      <h2 className="text-gray-700 font-medium">Your Checkpoints</h2>

      {urls.map((url) => {
        const isActive = url.isActive !== undefined ? url.isActive : true;
        const expiryDate = url.expiresAt ? new Date(url.expiresAt) : undefined;
        const isExpired = expiryDate && expiryDate < new Date();

        return (
          <CheckpointCard
            key={url.shortCode}
            url={url}
            isActive={isActive}
            isExpired={!!isExpired}
            expiryDate={expiryDate}
            isAuthenticated={isAuthenticated}
            onCopy={handleCopy}
            onVisit={handleVisit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onSetExpiry={handleSetExpiry}
            onDownloadQr={handleDownloadQr}
          />
        );
      })}
    </div>
  );
}

interface CheckpointCardProps {
  url: ShortenedUrlResponse;
  isActive: boolean;
  isExpired: boolean;
  expiryDate?: Date;
  isAuthenticated: boolean;
  onCopy: (text: string) => void;
  onVisit: (url: string) => void;
  onDelete: (shortCode: string) => void;
  onToggleActive: (url: ShortenedUrlResponse, currentState: boolean) => void;
  onSetExpiry: (url: ShortenedUrlResponse, date: Date | undefined) => void;
  onDownloadQr: (qrCodeUrl: string, name: string) => void;
}

function CheckpointCard({
  url,
  isActive,
  isExpired,
  expiryDate,
  isAuthenticated,
  onCopy,
  onVisit,
  onDelete,
  onToggleActive,
  onSetExpiry,
  onDownloadQr,
}: CheckpointCardProps) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const effectivelyActive = isActive && !isExpired;

  return (
    <Card
      className={`shadow-md border-0 backdrop-blur transition-all ${
        effectivelyActive ? 'bg-white/80' : 'bg-gray-50/80 opacity-80'
      }`}
    >
      <CardContent className="pt-5 pb-4 space-y-4">
        {/* Header: Name + Status + Delete */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate text-lg">
              {url.name || 'Unnamed checkpoint'}
            </h3>
            <p className="text-sm text-gray-500 truncate">
              {url.originalUrl}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant={effectivelyActive ? 'default' : 'secondary'}
              className={
                effectivelyActive
                  ? 'bg-green-100 text-green-700 hover:bg-green-100'
                  : 'bg-gray-100 text-gray-600'
              }
            >
              {isExpired ? 'Expired' : isActive ? 'Active' : 'Inactive'}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(url.shortCode)}
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Delete checkpoint"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Carrier Tabs */}
        <Tabs defaultValue="short-url" className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-9">
            <TabsTrigger value="short-url" className="text-xs">
              <Link2 className="w-3.5 h-3.5 mr-1.5" />
              Short URL
            </TabsTrigger>
            <TabsTrigger value="qr-code" className="text-xs">
              <QrCode className="w-3.5 h-3.5 mr-1.5" />
              QR Code
            </TabsTrigger>
          </TabsList>

          {/* Short URL Tab */}
          <TabsContent value="short-url" className="mt-3">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
                <span className="text-blue-600 text-sm font-mono truncate block">
                  {url.shortUrl}
                </span>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => onCopy(url.shortUrl)}
                className="h-9 w-9 shrink-0"
                title="Copy"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => onVisit(url.shortUrl)}
                className="h-9 w-9 shrink-0"
                title="Open"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </TabsContent>

          {/* QR Code Tab */}
          <TabsContent value="qr-code" className="mt-3">
            <div className="flex items-center gap-4">
              <div className="bg-white p-2 rounded-lg shadow-sm border shrink-0">
                <img
                  src={url.qrCodeUrl}
                  alt="QR Code"
                  className="w-24 h-24"
                />
              </div>
              <div className="flex-1 space-y-2">
                <p className="text-xs text-gray-500">
                  Scan to visit your checkpoint
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDownloadQr(url.qrCodeUrl!, url.name || 'checkpoint')}
                  className="w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Settings Row: Status Toggle + Expiry */}
        <div className="flex items-center justify-between pt-2 border-t">
          {isAuthenticated ? (
            <>
              {/* Status Toggle - Authenticated */}
              <div className="flex items-center gap-2">
                <Power className={`w-4 h-4 ${effectivelyActive ? 'text-green-600' : 'text-gray-400'}`} />
                <span className="text-sm text-gray-600">Active</span>
                <Switch
                  checked={isActive}
                  onCheckedChange={() => onToggleActive(url, isActive)}
                  disabled={isExpired}
                />
              </div>

              {/* Expiry Date - Authenticated */}
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`text-xs ${isExpired ? 'text-red-600' : 'text-gray-600'}`}
                  >
                    <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
                    {expiryDate
                      ? isExpired
                        ? `Expired ${format(expiryDate, 'MMM d')}`
                        : `Expires ${format(expiryDate, 'MMM d')}`
                      : 'Set expiry'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={expiryDate}
                    onSelect={(date) => {
                      onSetExpiry(url, date);
                      setDatePickerOpen(false);
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                  {expiryDate && (
                    <div className="p-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => {
                          onSetExpiry(url, undefined);
                          setDatePickerOpen(false);
                        }}
                      >
                        Remove expiry
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </>
          ) : (
            <>
              {/* Status Toggle - Not Authenticated (Gated) */}
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-gray-500 hover:text-blue-600"
                onClick={() => toast.info('Sign in to activate or deactivate checkpoints')}
              >
                <Lock className="w-3.5 h-3.5 mr-1.5" />
                <Power className="w-3.5 h-3.5 mr-1" />
                Active/Inactive
              </Button>

              {/* Expiry Date - Not Authenticated (Gated) */}
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-gray-500 hover:text-blue-600"
                onClick={() => toast.info('Sign in to set expiry dates for checkpoints')}
              >
                <Lock className="w-3.5 h-3.5 mr-1.5" />
                <CalendarClock className="w-3.5 h-3.5 mr-1" />
                Set expiry
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
